import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Boxes, FileText, Briefcase, Inbox, Users, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OrayaMark } from "@/components/site/primitives";

const NAV = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/dashboard/products", label: "Systems", icon: Boxes },
  { to: "/admin/dashboard/insights", label: "Insights", icon: FileText },
  { to: "/admin/dashboard/careers", label: "Careers", icon: Briefcase },
  { to: "/admin/dashboard/applications", label: "Applications", icon: Users },
  { to: "/admin/dashboard/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/dashboard/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading || user === null) {
    return <div data-theme="dark" className="min-h-screen bg-oraya-black flex items-center justify-center"><OrayaMark size={36} spin /></div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;

  const doLogout = async () => { await logout(); navigate("/admin", { replace: true }); };

  return (
    <div data-theme="dark" className="min-h-screen bg-oraya-black flex">
      <aside className="w-60 shrink-0 bg-oraya-graphite border-r border-white/[0.06] hidden md:flex flex-col fixed inset-y-0">
        <div className="px-6 py-6 flex items-center gap-2.5 border-b border-white/[0.06]">
          <OrayaMark size={24} />
          <span className="display font-medium tracking-[0.2em] text-oraya-mist">ORAYA</span>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/[0.06] text-oraya-mist" : "text-oraya-slate hover:text-oraya-mist hover:bg-white/[0.03]"}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/[0.06]">
          <div className="px-3 py-2 text-xs text-oraya-slate truncate">{user.email}</div>
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-oraya-slate hover:text-red-400 hover:bg-white/[0.03] transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-oraya-graphite border-b border-white/[0.06] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2"><OrayaMark size={22} /><span className="display tracking-[0.2em] text-oraya-mist text-sm">ADMIN</span></div>
        <button onClick={doLogout} className="text-oraya-slate"><LogOut className="w-5 h-5" /></button>
      </div>
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-oraya-graphite border-t border-white/[0.06] flex justify-around py-2">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `p-2 ${isActive ? "text-oraya-mist" : "text-oraya-slate"}`}>
            <n.icon className="w-5 h-5" />
          </NavLink>
        ))}
      </div>

      <main className="flex-1 md:ml-60 p-6 md:p-10 pt-20 md:pt-10 pb-24 md:pb-10">
        <Outlet />
      </main>
    </div>
  );
}
